/**
 * The Plant time form: the plant's zone, when its production day starts, its week start and its shift calendar, as
 * the settings page and the onboarding step edit them (GET and PUT /v1/twin/plant-time). A day start is a time and a
 * choice: the day starts at that time on its own date, or the evening before, counting toward the next date (the night
 * shift starting 22:30 works the next date), which the server stores signed ("-01:30"). A save sends only what
 * changed, so a page that edits the day start never wipes the shift calendar. Every word a person reads is here.
 * Pure.
 */
import { ApiClientError } from "@/lib/api-client";
import {
  dayStartWallClock,
  dayStartWords,
  formatDayStart,
  parseDayStart,
  parseHHMM,
  plantDayOf,
  plantWeekStartOf,
  type PlantTime,
  type PlantTimeChange,
  type ShiftBoundary,
} from "@/lib/shift-calendar";

/**
 * One shift row of the form: its own key (for React), the plant's name for it, its start ("HH:MM"), and when its work
 * ends ("HH:MM"; empty or absent when not set).
 */
export interface ShiftRow {
  key: string;
  label: string;
  start: string;
  end?: string;
}

/** What the page says while a shift has no end: the gateways then fetch updates at any time, slowly. */
export const SHIFT_ENDS_WARNING = "Set shift end times so gateways know when to fetch updates.";

/** Whether the shifts need the warning: there are shifts, and one has no end. */
export function shiftEndsMissing(shifts: { end?: string }[]): boolean {
  return shifts.length > 0 && shifts.some((s) => !(s.end ?? "").trim());
}

/** The form: every field as the page edits it. */
export interface PlantTimeForm {
  zone: string;
  /** The time on the plant's wall clock the day starts ("HH:MM"). */
  dayStart: string;
  /** The day starts the evening before and counts toward the next date. */
  eveningBefore: boolean;
  /** The plant week's first day, an ISO weekday (1 Monday .. 7 Sunday); null when none is chosen (Monday). */
  weekStart: number | null;
  shifts: ShiftRow[];
}

const DAY = 1440;
const NOON = 720;

/** The words of the evening choice, with the time it is for. */
export function eveningWords(time: string): string {
  return `The night shift starting ${time || "22:30"} counts toward the next date`;
}

/** The ISO weekdays as the week-start choice names them. */
export const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 7, label: "Sunday" },
];

let rowSeq = 0;
const rowRun = Math.random().toString(36).slice(2, 8);
/**
 * A new row's key, unique within the page and across reloads of a form kept in the browser (onboarding keeps its
 * form); a stored shift's key is its place in the stored calendar.
 */
export function newShiftKey(): string {
  rowSeq += 1;
  return `added-${rowRun}-${rowSeq}`;
}

const mod = (n: number, m: number) => ((n % m) + m) % m;

/**
 * A shift start as the time input shows it ("06:00" for a stored "6:00", which the server reads as the same minute);
 * a start that does not read stays as it is.
 */
function startText(start: string): string {
  const m = parseHHMM(start);
  return m === null ? start.trim() : dayStartWallClock(m);
}

/**
 * Shifts in the plant's day order: from the day's start (signed minutes, as the server stores it), so a shift that
 * starts the evening before comes first. A shift whose start does not read goes last, in the order it was given.
 */
export function shiftsInDayOrder<T extends { start: string }>(rows: T[], dayStart: number): T[] {
  const at = (r: T) => {
    const m = parseHHMM(r.start);
    return m === null ? Infinity : mod(m - dayStart, DAY);
  };
  return rows
    .map((r, i) => ({ r, i, t: at(r) }))
    .sort((a, b) => a.t - b.t || a.i - b.i)
    .map((x) => x.r);
}

/**
 * The form for a plant time. A plant with no zone has none in the form: nothing guesses a zone (#1144 review, MEDIUM 3).
 * Set up from a laptop that is not at the plant, the browser's zone is the wrong one, imports read their naive times on
 * it, and the stored rows keep those times until a re-import repairs them. The page offers the browser's zone as a
 * suggestion the person confirms (browserZone, the Use button), and a form with no zone is held (formProblems).
 */
export function formOf(pt: PlantTime | null | undefined): PlantTimeForm {
  const raw = (pt?.production_day_start ?? "").trim();
  const start = raw ? parseDayStart(raw) : 0;
  const week = plantWeekStartOf(pt);
  const signed = start ?? 0;
  // A stored shift's key is its place in the stored calendar, so the same plant time gives the same rows every render.
  const rows = (pt?.shift_calendar ?? []).map((s, i) => ({
    key: `stored-${i}`,
    label: s.label ?? "",
    start: startText(s.start ?? ""),
    end: (s.end ?? "").trim() ? startText(s.end ?? "") : undefined,
  }));
  return {
    zone: pt?.timezone?.trim() ?? "",
    dayStart: dayStartWallClock(signed),
    eveningBefore: signed < 0,
    weekStart: week.plant ? week.weekday : null,
    shifts: shiftsInDayOrder(rows, signed),
  };
}

/**
 * What to say of a stored day start the platform cannot read (one from before the day start was signed, like
 * "13:00"): the platform reads such a clock as none, UTC days, until it is set again. The form shows midnight for it,
 * and any save writes the form's day start (changeOf: the merged PUT validates the whole record), so the note says
 * that before the person saves. null when it reads, or is unset.
 */
export function storedDayStartNote(pt: PlantTime | null | undefined): string | null {
  const raw = (pt?.production_day_start ?? "").trim();
  if (!raw || parseDayStart(raw) !== null) return null;
  return (
    `The stored production day start, "${raw}", does not read, so the platform keeps UTC days for now. ` +
    "Saving will reset the day start to midnight unless you set it below."
  );
}

/** The day start the form stores, signed as the server reads it, or what is wrong with it in plain words. */
export function dayStartValueOf(f: Pick<PlantTimeForm, "dayStart" | "eveningBefore">): {
  value: string | null;
  problem: string | null;
} {
  const m = parseHHMM(f.dayStart ?? "");
  if (m === null) return { value: null, problem: "Write when the day starts as a time, like 06:00." };
  if (f.eveningBefore) {
    if (m <= NOON) {
      return {
        value: null,
        problem: `A day that starts the evening before starts after 12:00, as a night shift does: ${f.dayStart} is not.`,
      };
    }
    return { value: formatDayStart(m - DAY), problem: null };
  }
  if (m >= NOON) {
    return {
      value: null,
      problem: `A day that starts at ${f.dayStart} starts the evening before: choose "${eveningWords(f.dayStart)}".`,
    };
  }
  return { value: formatDayStart(m), problem: null };
}

/** Whether the browser knows a zone. */
export function zoneKnown(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** What stops the form from being saved, in plain words, in the order the page shows its fields. */
export function formProblems(f: PlantTimeForm, known: (zone: string) => boolean = zoneKnown): string[] {
  const out: string[] = [];
  const zone = f.zone.trim();
  if (!zone) out.push("Choose the plant's time zone.");
  else if (!known(zone)) out.push(`"${zone}" is not a time zone this browser knows: choose one from the list.`);
  const day = dayStartValueOf(f);
  if (day.problem) out.push(day.problem);
  const seen = new Set<number>();
  f.shifts.forEach((s, i) => {
    const label = s.label.trim();
    if (!label) {
      out.push(`Shift ${i + 1} needs a name.`);
      return;
    }
    const m = parseHHMM(s.start);
    if (m === null) {
      out.push(`Shift ${label} needs a start time.`);
      return;
    }
    if (seen.has(m)) out.push(`Two shifts start at ${s.start}.`);
    seen.add(m);
    const end = (s.end ?? "").trim();
    if (end && parseHHMM(end) === null) out.push(`Write when shift ${label} ends as a time, like 14:00, or leave it empty.`);
  });
  return out;
}

/** The calendar the form stores: every shift trimmed, in the plant's day order, with its end when it has one. */
export function calendarOf(f: PlantTimeForm): ShiftBoundary[] {
  const start = parseDayStart(dayStartValueOf(f).value ?? "") ?? 0;
  return shiftsInDayOrder(f.shifts, start).map((s) => {
    const end = (s.end ?? "").trim();
    return end ? { start: s.start.trim(), label: s.label.trim(), end } : { start: s.start.trim(), label: s.label.trim() };
  });
}

/** A calendar as a comparable key: its shifts in start order, trimmed, each start and end in its one written form. */
function calendarKey(cal: ShiftBoundary[]): string {
  return JSON.stringify(
    cal
      .map((s) => ({
        m: parseHHMM((s.start ?? "").trim()) ?? -1,
        start: startText(s.start ?? ""),
        label: (s.label ?? "").trim(),
        end: (s.end ?? "").trim() ? startText(s.end ?? "") : "",
      }))
      .sort((a, b) => a.m - b.m || a.label.localeCompare(b.label))
      .map(({ start, label, end }) => ({ start, label, end })),
  );
}

/**
 * What a save sends: only the fields the form changed, so the shift calendar, the zone and the week start are never
 * wiped by a save of another field. No day start and 00:00 are the same (midnight). Empty when nothing changed.
 */
export function changeOf(pt: PlantTime | null | undefined, f: PlantTimeForm): PlantTimeChange {
  const out: PlantTimeChange = {};
  const zone = f.zone.trim();
  if (zone !== (pt?.timezone ?? "").trim()) out.timezone = zone;
  const day = dayStartValueOf(f).value;
  const raw = (pt?.production_day_start ?? "").trim();
  const stored = raw ? parseDayStart(raw) : 0;
  if (day !== null && (stored === null || parseDayStart(day) !== stored)) out.production_day_start = day;
  const week = plantWeekStartOf(pt);
  const storedWeek = week.plant ? week.weekday : null;
  if (f.weekStart !== storedWeek) out.production_week_start = f.weekStart;
  const cal = calendarOf(f);
  if (calendarKey(cal) !== calendarKey(pt?.shift_calendar ?? [])) out.shift_calendar = cal;
  return out;
}

/** One shift of today on the plant's clock, as the preview lists it. */
export interface TodaysShift {
  label: string;
  from: string;
  to: string;
}

/** Today on the plant's clock: its plant day, when it runs, and its shifts. */
export interface Today {
  day: string;
  from: string;
  to: string;
  rows: TodaysShift[];
}

/** Minutes from the midnight of the plant day's date, on its wall clock, said: "22:30 the evening before". */
function wallWords(t: number): string {
  const clock = dayStartWallClock(t);
  if (t < 0) return `${clock} the evening before`;
  if (t >= DAY) return `${clock} the next day`;
  return clock;
}

/**
 * Today's shifts on the plant's clock as the form has it: the plant day that holds `now`, from its start to the next
 * day's, and each shift from its start to the next shift's, in day order (the last running to the first one's next
 * start, past the day's end when the day does not start at a shift). null when the form has no zone the browser knows
 * or no day start that reads.
 */
export function todaysShifts(f: PlantTimeForm, now: Date): Today | null {
  const zone = f.zone.trim();
  const value = dayStartValueOf(f).value;
  if (!zone || !zoneKnown(zone) || value === null) return null;
  const start = parseDayStart(value) ?? 0;
  const day = plantDayOf({ timezone: zone, shift_calendar: [], production_day_start: value }, now.toISOString());
  if (!day) return null;
  const at = f.shifts
    .map((s) => ({ label: s.label.trim(), m: parseHHMM(s.start) }))
    .filter((s): s is { label: string; m: number } => s.label !== "" && s.m !== null)
    .map((s) => ({ label: s.label, t: start + mod(s.m - start, DAY) }))
    .sort((a, b) => a.t - b.t);
  const rows = at.map((s, i) => ({
    label: s.label,
    from: wallWords(s.t),
    to: wallWords(i + 1 < at.length ? at[i + 1].t : at[0].t + DAY),
  }));
  return { day, from: wallWords(start), to: wallWords(start + DAY), rows };
}

/**
 * How many closed plant days back a change of the clock cuts the briefs and roll-ups again (platform-core's
 * rollupAutoDays). Older periods keep the clock they were cut on.
 */
export const PLANT_TIME_RECUT_DAYS = 400;

/** What the page says a clock change reaches, and how far back (#1144 review, LOW 1). */
export function clockChangeWords(): string {
  return (
    "A change applies to what is imported from now on. The briefs and roll-ups cut their last " +
    `${PLANT_TIME_RECUT_DAYS} closed days again on the new clock after a save, so those days and weeks move with it; ` +
    "older periods keep the clock they were cut on."
  );
}

/** The browser's zone as a suggestion for the zone choice, never as a value. */
export function zoneSuggestionWords(browser: string): string {
  return `Your browser is set to ${browser}. That is the plant's zone only if you are at the plant.`;
}

/** The page that sets the plant time, for everyone. */
export const PLANT_TIME_PATH = "/app/settings?tab=plant-time";

/** A plant time form in one line: "America/New_York; the day starts at 22:30 the evening before; 3 shifts". */
export function plantTimeLine(f: PlantTimeForm): string {
  const value = dayStartValueOf(f).value;
  const day = (value !== null ? dayStartWords(value) : null) ?? f.dayStart;
  const n = f.shifts.length;
  const shifts = n === 0 ? "no shift calendar" : n === 1 ? "1 shift" : `${n} shifts`;
  return `${f.zone.trim()}; the day starts at ${day}; ${shifts}`;
}

/** A plant day as the preview names it: "Wednesday 30 September". */
export function dayWords(day: string): string {
  return new Date(`${day}T12:00:00Z`).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/**
 * The zones the zone choice offers: every zone the browser lists, UTC, and the given ones (the plant's own, which may
 * be an alias the browser does not list, and the browser's), each once.
 */
export function timeZoneOptions(extra: string[]): string[] {
  let listed: string[] = [];
  try {
    listed = Intl.supportedValuesOf("timeZone");
  } catch {
    listed = [];
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const z of [...extra, "UTC", ...listed]) {
    const v = z.trim();
    if (v && !seen.has(v)) {
      seen.add(v);
      out.push(v);
    }
  }
  return out;
}

/** The browser's own zone ("UTC" when it says none). */
export function browserZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/** What the page says when the plant time is not saved: the platform's own words where it refused the values. */
export function plantTimeSaveErrorText(err: unknown): string {
  if (err instanceof ApiClientError) {
    const body = err.body as { error?: unknown; detail?: unknown } | null;
    const detail = typeof body?.detail === "string" ? body.detail.trim() : "";
    if (err.status === 403) return "Only an org admin can change the plant time.";
    if (body?.error === "invalid_plant_time" && detail) return `The plant time was not saved: ${detail.replace(/\.$/, "")}.`;
    if (body?.error === "invalid_body") {
      return "The platform could not read what the page sent: read the page again and save once more.";
    }
  }
  return "The plant time was not saved. Check the values and try again.";
}
